import { AppSetting, AppSettingsTable, AppSettingInsert } from "@/types/settings";
import { AppSettingRow } from "@/types/appSettings";
import { platformApiFetch } from '@/services/platformApi/client';

/**
 * Fetch all application settings from the database
 */
export const fetchAppSettings = async (): Promise<AppSettingsTable[]> => {
  try {
    return await platformApiFetch<AppSettingsTable[]>('/v1/settings');
  } catch (error) {
    console.error("Error in fetchAppSettings:", error);
    throw error;
  }
};

/**
 * Fetch application settings by category
 */
export const fetchAppSettingsByCategory = async (category: string): Promise<{ key: string; value: string }[]> => {
  try {
    return await platformApiFetch<{ key: string; value: string }[]>(`/v1/settings/category/${encodeURIComponent(category)}`);
  } catch (error) {
    console.error("Error in fetchAppSettingsByCategory:", error);
    throw error;
  }
};

/**
 * Fetch a single application setting by key
 */
export const fetchAppSettingByKey = async (key: string): Promise<string | null> => {
  try {
    return await platformApiFetch<string | null>(`/v1/settings/key/${encodeURIComponent(key)}`);
  } catch (error) {
    console.error("Error in fetchAppSettingByKey:", error);
    throw error;
  }
};

/**
 * Fetch app settings by keys and categories for social media
 */
export const fetchSocialMediaSettings = async (): Promise<{ key: string; value: string }[]> => {
  try {
    return await platformApiFetch<{ key: string; value: string }[]>('/v1/settings/social-media');
  } catch (error) {
    console.error("Error in fetchSocialMediaSettings:", error);
    throw error;
  }
};

/**
 * Insert a new application setting
 */
export const insertAppSetting = async (setting: AppSettingInsert): Promise<void> => {
  try {
    await platformApiFetch('/v1/settings', {
      method: 'POST',
      body: JSON.stringify(setting),
    });
  } catch (error) {
    console.error("Error in insertAppSetting:", error);
    throw error;
  }
};

/**
 * Update an existing application setting
 */
export const updateAppSetting = async (id: string, value: string): Promise<void> => {
  try {
    await platformApiFetch(`/v1/settings/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    });
  } catch (error) {
    console.error("Error in updateAppSetting:", error);
    throw error;
  }
};

/**
 * Fetch specific legal settings
 */
export const fetchLegalSettings = async (): Promise<{
  last_updated_date: string;
}> => {
  try {
    const data = await platformApiFetch<AppSettingRow[]>('/v1/settings/legal');
    
    const setting = data?.[0];

    if (setting) {
      return { last_updated_date: setting.value };
    }
    
    // Default value if setting is not found
    return {
      last_updated_date: "June 14, 2025",
    };
  } catch (error) {
    console.error("Error in fetchLegalSettings:", error);
    // Return default values on error
    return {
      last_updated_date: "June 14, 2025",
    };
  }
};

/**
 * Check if a feature flag exists by key and category
 */
export const checkFeatureFlagExists = async (key: string, category: string = 'Feature Flags'): Promise<any> => {
  try {
    const flags = await platformApiFetch<AppSettingsTable[]>('/v1/settings/feature-flags');
    return flags.find((flag) => flag.key === key && flag.category === category) ?? null;
  } catch (error) {
    console.error("Error in checkFeatureFlagExists:", error);
    throw error;
  }
};

/**
 * Create or update a feature flag
 */
export const createOrUpdateFeatureFlag = async (
  key: string, 
  value: string, 
  description: string, 
  category: string = 'Feature Flags'
): Promise<void> => {
  try {
    await platformApiFetch('/v1/settings/feature-flags/upsert', {
      method: 'POST',
      body: JSON.stringify({ key, value, category, description }),
    });
  } catch (error) {
    console.error("Error in createOrUpdateFeatureFlag:", error);
    throw error;
  }
};
